import { Component, computed, effect, input, output, signal } from '@angular/core';
import { FormArray } from '@angular/forms';
import { TireReport } from '@app-model/evaluation';

@Component({
  selector: 'app-evaluation-axle',
  templateUrl: './evaluation-axle.component.html',
  styleUrls: ['./evaluation-axle.component.scss']
})
export class EvaluationAxleComponent {
 
  readonly quantityTires = input<2 | 4>(2);
  readonly index = input<number>(0);
  readonly activeAxle = input<boolean>(false);
  readonly isReportItem = input<boolean>(false);
  readonly tiresFormArray = input<FormArray | undefined>(undefined);
  readonly permitiEditing = input<boolean>(true);

  readonly tireCountChange = output<number>();

  readonly tires = signal<TireReport[]>([]);
  readonly isActiveAxle = signal<boolean>(false);

  private readonly colorsArray = [
    { classification: 'NON_EXISTENT', color: 'white' },
    { classification: 'GOOD', color: '#16B097' },
    { classification: 'REGULAR', color: '#FFC107' },
    { classification: 'BAD', color: '#FF5A5A' }
  ] as const;

  private readonly colors = this.colorsArray.reduce((acc, item) => {
    acc[item.classification] = item.color;
    return acc;
  }, {} as Record<string, string>);

  readonly message = computed(() => {
    const idx = this.index();
    return idx === 0 ? `Eixo ${idx + 1} (FRENTE)` : `Eixo ${idx + 1}`;
  });

  readonly isMobile = signal(window.innerWidth < 768);

  constructor() {
    // Effect para sincronizar activeAxle input com o signal interno
    effect(() => {
      this.isActiveAxle.set(this.activeAxle());
    }, { allowSignalWrites: true });

    // Effect para inicializar os pneus quando o tiresFormArray mudar
    effect(() => {
      const formArray = this.tiresFormArray();
      const isReport = this.isReportItem();

      if (isReport && formArray?.controls?.length) {
        const tiresData: TireReport[] = formArray.controls.map((tire) => {
          const tireReport = { ...tire.value } as TireReport;
          tireReport.color = this.colors[tireReport.classification];
          return tireReport;
        });
        this.tires.set(tiresData);
      }
    }, { allowSignalWrites: true });
  }

  addOrRemoveTires(): void {
    this.isActiveAxle.update(v => !v);
    this.tireCountChange.emit(this.isActiveAxle() ? 4 : 2);
  }

  changeTireClassification(tireIndex: number): void {
    const currentTires = this.tires();
    if (!this.isReportItem() || !this.permitiEditing() || !currentTires.length) {
      return;
    }

    const position = !this.isActiveAxle() && tireIndex === 2 ? 2 : tireIndex + 1;

    const tireIdx = currentTires.findIndex(t => t.tirePosition === position);
    if (tireIdx === -1) {
      console.warn(`Pneu na posição ${position} não encontrado. Tires:`, currentTires);
      return;
    }

    const tire = currentTires[tireIdx];
    const newClassification = this.nextClassification(tire);
    const newColor = this.colors[newClassification];

    this.tires.update(tires => {
      const updated = [...tires];
      updated[tireIdx] = {
        ...updated[tireIdx],
        classification: newClassification,
        color: newColor
      };
      return updated;
    });

    const formArray = this.tiresFormArray();
    const control = formArray?.controls.find(c => c.value.tirePosition === position);
    if (control) {
      control.get('classification')?.setValue(newClassification);
    }
  }

  private nextClassification(tire: TireReport): 'GOOD' | 'REGULAR' | 'BAD' | 'NON_EXISTENT' {
    switch (tire.classification) {
      case 'NON_EXISTENT':
        return 'GOOD';
      case 'GOOD':
        return 'REGULAR';
      case 'REGULAR':
        return 'BAD';
      case 'BAD':
        return 'NON_EXISTENT';
      default:
        return 'NON_EXISTENT';
    }
  }
}
